import { PottedPlant } from '../parts/greenery'
import { Keyboard, Monitor, Mouse } from '../parts/tech'
import { PaperStack } from '../parts/props'
import {
  DESK_HEIGHT,
  Grommet,
  ModestyPanel,
  PanelLegs,
  Pedestal,
  PrivacyPanel,
  WorkTop,
} from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

const WIDTH = 1.6
const DEPTH = 0.8

/**
 * REFERENCE ASSET — the canonical example of the authoring contract.
 *
 * Origin at the centre of the 1.6 x 0.8 footprint, y = 0 on the floor, front
 * (the side the chair goes) facing +Z. Nothing here sets colours directly: the
 * accent reaches the privacy screen through the `accent` prop, everything else
 * is a semantic tone.
 */
function DeskSingle({ accent, variant = 'screen' }: AssetContext) {
  const dressed = variant === 'dressed'
  const withScreen = variant !== 'plain'

  return (
    <group>
      <WorkTop width={WIDTH} depth={DEPTH} />
      <PanelLegs width={WIDTH} depth={DEPTH} sides="left" />
      <ModestyPanel width={WIDTH - 0.5} z={-DEPTH / 2 + 0.09} />
      <Grommet x={0.36} z={-DEPTH / 2 + 0.12} />

      <group position={[WIDTH / 2 - 0.26, 0, -0.05]}>
        <Pedestal depth={DEPTH - 0.16} height={0.62} />
      </group>

      {withScreen ? (
        <PrivacyPanel width={WIDTH} accent={accent} z={-DEPTH / 2 + 0.03} />
      ) : null}

      {dressed ? (
        <group>
          <group position={[-0.12, 0, -0.2]}>
            <Monitor base={DESK_HEIGHT} />
          </group>
          <group position={[-0.12, 0, 0.12]}>
            <Keyboard base={DESK_HEIGHT} />
          </group>
          <group position={[0.22, 0, 0.14]}>
            <Mouse base={DESK_HEIGHT} />
          </group>
          <group position={[-0.62, 0, 0.16]} rotation={[0, 0.4, 0]}>
            <PaperStack base={DESK_HEIGHT} />
          </group>
          <group position={[0.6, DESK_HEIGHT, -0.24]}>
            <PottedPlant variant="desk" />
          </group>
        </group>
      ) : null}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'desk-single',
  name: 'Escritorio individual',
  category: 'desks',
  footprint: [WIDTH, DEPTH],
  height: DESK_HEIGHT + 0.41,
  surfaceHeight: DESK_HEIGHT,
  seats: 1,
  occupantSide: 'front',
  accentable: true,
  variants: [
    { id: 'plain', name: 'Sin panel' },
    { id: 'screen', name: 'Con panel' },
    { id: 'dressed', name: 'Equipado' },
  ],
  keywords: ['escritorio', 'puesto', 'desk', 'workstation', 'mesa de trabajo'],
  Component: DeskSingle,
}
